/**
 * Custom Wishlist for Shopify Dawn (OS 2.0)
 * ------------------------------------------------
 * - Vanilla JS, no dependencies
 * - localStorage-backed, stores product HANDLES (not IDs)
 * - Event delegation for heart buttons (works with AJAX-rendered cards,
 *   quick-add modals, infinite scroll, section reloads)
 * - Dawn compatible: listens for shopify:section:load
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'shopify_wishlist';
  var WISHLIST_EVENT = 'wishlist:updated';

  /* ==========================================================
     Storage layer
  ========================================================== */
  var Wishlist = {
    get: function () {
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        var parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },

    save: function (handles) {
      var unique = [];
      for (var i = 0; i < handles.length; i++) {
        if (handles[i] && unique.indexOf(handles[i]) === -1) unique.push(handles[i]);
      }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(unique));
      } catch (e) {
        /* localStorage unavailable (private mode / quota) - fail silently */
      }
      Wishlist.broadcast(unique);
      return unique;
    },

    has: function (handle) {
      return Wishlist.get().indexOf(handle) !== -1;
    },

    add: function (handle) {
      var items = Wishlist.get();
      if (items.indexOf(handle) === -1) {
        items.push(handle);
        Wishlist.save(items);
      }
      return items;
    },

    remove: function (handle) {
      var items = Wishlist.get().filter(function (h) {
        return h !== handle;
      });
      Wishlist.save(items);
      return items;
    },

    toggle: function (handle) {
      return Wishlist.has(handle) ? Wishlist.remove(handle) : Wishlist.add(handle);
    },

    count: function () {
      return Wishlist.get().length;
    },

    broadcast: function (items) {
      document.dispatchEvent(
        new CustomEvent(WISHLIST_EVENT, { detail: { items: items, count: items.length } })
      );
    }
  };

  // Expose for debugging / theme editor / other scripts
  window.Wishlist = Wishlist;

  /* ==========================================================
     Utilities
  ========================================================== */
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        var format =
          (window.theme && window.theme.moneyFormat) ||
          window.theme_money_format ||
          '${{amount}}';
        return window.Shopify.formatMoney(cents, format);
      }
    } catch (e) {
      /* fall through to default formatting */
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ==========================================================
     Heart button UI sync
  ========================================================== */
  function syncHeartButton(btn, handle) {
    var active = Wishlist.has(handle);
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    var label = active
      ? btn.getAttribute('data-label-remove') || 'Remove from wishlist'
      : btn.getAttribute('data-label-add') || 'Add to wishlist';
    btn.setAttribute('aria-label', label);
    btn.setAttribute('title', label);
  }

  function syncAllHeartButtons(root) {
    var scope = root || document;
    var buttons = scope.querySelectorAll('[data-wishlist-toggle]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var handle = btn.getAttribute('data-product-handle');
      if (handle) syncHeartButton(btn, handle);
    }
  }

  function animateHeart(btn) {
    btn.classList.remove('wishlist-pop');
    // Force reflow so the animation can restart on rapid clicks
    void btn.offsetWidth;
    btn.classList.add('wishlist-pop');
    window.setTimeout(function () {
      btn.classList.remove('wishlist-pop');
    }, 450);
  }

  /* ==========================================================
     Header count badge sync
  ========================================================== */
  function syncCountBadges(count) {
    var c = typeof count === 'number' ? count : Wishlist.count();
    var badges = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = c;
      badges[i].classList.toggle('is-visible', c > 0);
      badges[i].setAttribute('data-count', c);
    }
  }

  /* ==========================================================
     Wishlist page: fetch + render products by handle
  ========================================================== */
  function fetchProductByHandle(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .catch(function () {
        return null;
      });
  }

  function buildWishlistCard(product) {
    var variant = (product.variants && product.variants[0]) || null;
    var price = variant ? variant.price : product.price;
    var compareAt = variant ? variant.compare_at_price : product.compare_at_price;
    var onSale = !!(compareAt && compareAt > price);
    var image = product.featured_image || (product.images && product.images[0]) || '';
    var available = !!product.available;
    var title = escapeHtml(product.title);
    var url = '/products/' + product.handle;

    var article = document.createElement('article');
    article.className = 'wishlist-card';
    article.setAttribute('data-wishlist-item', product.handle);
    article.setAttribute('role', 'listitem');

    article.innerHTML =
      '<div class="wishlist-card__media">' +
        '<a href="' + url + '" class="wishlist-card__link" aria-label="' + title + '">' +
          (onSale ? '<span class="wishlist-card__badge wishlist-card__badge--sale">Sale</span>' : '') +
          (!available ? '<span class="wishlist-card__badge wishlist-card__badge--sold">Sold out</span>' : '') +
          '<img src="' + image + '" alt="' + title + '" loading="lazy" width="400" height="400" class="wishlist-card__image">' +
        '</a>' +
        '<button type="button" class="wishlist-card__remove" data-wishlist-remove="' + product.handle + '" aria-label="Remove ' + title + ' from wishlist">' +
          '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M18.3 5.71L12 12.01l-6.3-6.3-1.41 1.41L10.59 13.4l-6.3 6.3 1.41 1.41 6.3-6.3 6.3 6.3 1.41-1.41-6.3-6.3 6.3-6.3z"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="wishlist-card__content">' +
        '<h3 class="wishlist-card__title"><a href="' + url + '">' + title + '</a></h3>' +
        '<div class="wishlist-card__price">' +
          '<span class="wishlist-card__price-current' + (onSale ? ' is-sale' : '') + '">' + formatMoney(price) + '</span>' +
          (onSale ? '<span class="wishlist-card__price-compare">' + formatMoney(compareAt) + '</span>' : '') +
        '</div>' +
        '<div class="wishlist-card__actions">' +
          '<button type="button" class="wishlist-card__add-to-cart button" data-wishlist-add-to-cart data-variant-id="' + (variant ? variant.id : '') + '"' + (!available ? ' disabled' : '') + '>' +
            (available ? 'Add to cart' : 'Sold out') +
          '</button>' +
        '</div>' +
      '</div>';

    return article;
  }

  function renderWishlistPage() {
    var container = document.querySelector('[data-wishlist-grid]');
    if (!container) return;

    var emptyState = document.querySelector('[data-wishlist-empty]');
    var loadingState = document.querySelector('[data-wishlist-loading]');
    var handles = Wishlist.get();

    if (handles.length === 0) {
      container.innerHTML = '';
      container.hidden = true;
      if (loadingState) loadingState.hidden = true;
      if (emptyState) emptyState.hidden = false;
      return;
    }

    if (loadingState) loadingState.hidden = false;
    if (emptyState) emptyState.hidden = true;
    container.hidden = true;

    Promise.all(handles.map(fetchProductByHandle)).then(function (results) {
      var validHandles = [];
      var fragment = document.createDocumentFragment();

      results.forEach(function (product, i) {
        if (product) {
          validHandles.push(handles[i]);
          fragment.appendChild(buildWishlistCard(product));
        }
      });

      // Requirement: automatically remove unavailable / deleted products
      if (validHandles.length !== handles.length) {
        Wishlist.save(validHandles);
      }

      container.innerHTML = '';

      if (validHandles.length === 0) {
        container.hidden = true;
        if (emptyState) emptyState.hidden = false;
      } else {
        container.appendChild(fragment);
        container.hidden = false;
        if (emptyState) emptyState.hidden = true;
      }
      if (loadingState) loadingState.hidden = true;
    });
  }

  /* ==========================================================
     Event delegation (single listener, works for dynamic content)
  ========================================================== */
  document.addEventListener('click', function (event) {
    // 1) Heart toggle (product cards, grids, quick view, etc.)
    var heartBtn = event.target.closest('[data-wishlist-toggle]');
    if (heartBtn) {
      event.preventDefault();
      event.stopPropagation();

      var handle = heartBtn.getAttribute('data-product-handle');
      if (!handle) return;

      Wishlist.toggle(handle);
      syncHeartButton(heartBtn, handle);
      animateHeart(heartBtn);

      if (document.querySelector('[data-wishlist-page]')) {
        renderWishlistPage();
      }
      return;
    }

    // 2) Remove button on the wishlist page
    var removeBtn = event.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      event.preventDefault();
      var removeHandle = removeBtn.getAttribute('data-wishlist-remove');
      Wishlist.remove(removeHandle);
      syncAllHeartButtons(); // keep any visible hearts elsewhere in sync

      var card = removeBtn.closest('[data-wishlist-item]');
      if (card) {
        card.classList.add('is-removing');
        window.setTimeout(function () {
          card.remove();
          if (Wishlist.count() === 0) renderWishlistPage();
        }, 220);
      }
      return;
    }

    // 3) Add to cart button on the wishlist page
    var addBtn = event.target.closest('[data-wishlist-add-to-cart]');
    if (addBtn && !addBtn.disabled) {
      event.preventDefault();
      var variantId = addBtn.getAttribute('data-variant-id');
      if (!variantId) return;

      var originalText = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw new Error(data && data.description ? data.description : 'Add to cart failed');
            return data;
          });
        })
        .then(function () {
          addBtn.textContent = 'Added ✓';
          // Notify Dawn's cart drawer / cart page to refresh if present
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.documentElement.dispatchEvent(new CustomEvent('cart:build'));
          window.setTimeout(function () {
            addBtn.textContent = originalText;
            addBtn.disabled = false;
          }, 1600);
        })
        .catch(function () {
          addBtn.textContent = 'Sold out';
          window.setTimeout(function () {
            addBtn.textContent = originalText;
            addBtn.disabled = false;
          }, 1600);
        });
    }
  });

  // Keep the count badge in sync from anywhere the wishlist changes
  document.addEventListener(WISHLIST_EVENT, function (e) {
    syncCountBadges(e.detail.count);
  });

  /* ==========================================================
     Init + Dawn compatibility
  ========================================================== */
  function init() {
    syncAllHeartButtons();
    syncCountBadges();
    if (document.querySelector('[data-wishlist-page]')) {
      renderWishlistPage();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Dawn re-renders sections in the theme editor / after AJAX cart updates.
  // Re-sync heart states whenever new markup shows up.
  document.addEventListener('shopify:section:load', function (event) {
    syncAllHeartButtons(event.target || document);
  });
  document.addEventListener('cart:refresh', function () {
    syncAllHeartButtons();
  });

  // Catch dynamically injected cards (quick-add modal, infinite scroll,
  // predictive search results) without polling.
  if ('MutationObserver' in window) {
    var observer = new MutationObserver(function (mutations) {
      var needsSync = false;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-wishlist-toggle]')) {
            needsSync = true;
            break;
          }
          if (node.querySelector && node.querySelector('[data-wishlist-toggle]')) {
            needsSync = true;
            break;
          }
        }
        if (needsSync) break;
      }
      if (needsSync) syncAllHeartButtons();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();